import { Container, ColorMatrixFilter, CanvasTextMetrics, Graphics, NineSliceSprite, Rectangle, Text, TextStyle, type FederatedPointerEvent } from 'pixi.js';
import { flex, clamp, type Size } from './layout';
import { presentation, type UiStyle } from './presets';
import { uiScale, textPixels, scaleRevision, textResolution } from './scale';
import { EditModel } from './editModel';
import type { Sprites } from '../assets';

export type Child = Widget | string | number | null | undefined | false;
export interface UiPointerEvent {
  target: Widget; clientX: number; clientY: number; button: number; deltaY: number;
  shiftKey: boolean; ctrlKey: boolean; preventDefault(): void; stopPropagation(): void;
}
export interface UiEvents {
  keydown: KeyboardEvent; blur: Event; input: Event; change: Event; submit: Event;
  mousedown: UiPointerEvent; mousemove: UiPointerEvent; mouseenter: UiPointerEvent; mouseleave: UiPointerEvent;
  click: UiPointerEvent; dblclick: UiPointerEvent; contextmenu: UiPointerEvent; wheel: UiPointerEvent;
}
export interface WidgetOptions { class?: string; style?: UiStyle; testId?: string; [key: string]: unknown }
export let activeRoot: UiRoot | null = null;
let skin: Sprites;
const liveWidgets = new Set<Widget>();
const retired = new Set<Widget>();
export function useSkin(s: Sprites): void { skin = s; }
export const colors: Record<string, string> = { text: '#ece4d4', title: '#f6ecd8', dim: '#a19a90', accent: '#e8c068', gold: '#c99a50', error: '#f06058', good: '#96dc5a', hint: '#d8c896', desc: '#a8a096', line: '#4a382a' };
export function color(value: string | undefined, fallback = '#ece4d4'): string {
  return value?.replace(/var\(--([\w-]+)\)/g, (_, key) => colors[key] ?? fallback) || fallback;
}
function numeric(value: string | undefined, available: number, font = textPixels()): number | undefined {
  if (value === undefined || value === '' || value === 'auto' || value === 'none') return undefined;
  if (value.endsWith('vw')) return parseFloat(value) / 100 * innerWidth / uiScale;
  if (value.endsWith('vh')) return parseFloat(value) / 100 * innerHeight / uiScale;
  if (value.endsWith('%')) return parseFloat(value) / 100 * available;
  if (value.endsWith('em')) return parseFloat(value) * font;
  const n = Number.parseFloat(value); return Number.isFinite(n) ? n : undefined;
}
/** How much a flex item grows: its flex, or 1 for margin-left: auto (it takes the free space; see the hotkey preset). */
function growOf(c: Widget): number { const s = c.effective(); return s.marginLeft === 'auto' ? 1 : Number(s.flex) || 0; }
/** The narrowest a flex item may shrink. Icons, tooltip values and fixed buttons keep their size: a button squeezed by a
 *  full-width field would show only "...". Growing buttons (a .buttons row) still share space. */
function minWidthOf(c: Widget, preferred: number, available: number): number {
  const own = numeric(c.effective().minWidth, available);
  if (own !== undefined) return own;
  return ['icon', 'character', 'raster'].includes(c.kind) || c.classList.contains('tip-value') || (c.kind === 'button' && !growOf(c)) ? preferred : 0;
}
function padding(s: UiStyle): number[] {
  const all = (s.padding ?? '0').split(/\s+/).map(v => numeric(v, 0) ?? 0);
  const [t, r = t, b = t, l = r] = all;
  return [numeric(s.paddingTop, 0) ?? t, numeric(s.paddingRight, 0) ?? r, numeric(s.paddingBottom, 0) ?? b, numeric(s.paddingLeft, 0) ?? l];
}
class Variants {
  constructor(private owner: Widget) {}
  contains(name: string): boolean { return this.owner.className.split(/\s+/).includes(name); }
  add(...names: string[]): void { this.owner.className = [...new Set([...this.owner.className.split(/\s+/), ...names])].join(' '); }
  remove(...names: string[]): void { this.owner.className = this.owner.className.split(/\s+/).filter(n => !names.includes(n)).join(' '); }
  toggle(name: string, on = !this.contains(name)): boolean { if (on) this.add(name); else this.remove(name); return on; }
}

/** A retained Pixi widget. The visual layer never participates in widget layout. */
export class Widget extends Container {
  readonly contentLayer = new Container();
  readonly visualLayer = new Container();
  readonly widgets: Widget[] = [];
  readonly classList = new Variants(this);
  readonly style: UiStyle;
  private names = '';
  title = '';
  testId = '';
  nameHint = '';
  type = '';
  private inactive = false;
  get disabled(): boolean { return this.inactive; }
  set disabled(value: boolean) { if (value !== this.inactive) { this.inactive = value; this.invalidate(); } }
  checked = false;
  min = 0;
  max = 100;
  maxLength = 64;
  placeholder = '';
  /** A text field's name over the touch keyboard's preview (the placeholder when empty). */
  label = '';
  protected valueText = '';
  protected labelText: Text | null = null;
  private labelStyleKey = '';
  private scrollDrag: { y: number; origin: number; factor: number } | null = null;
  private background: NineSliceSprite | null = null;
  private colorFilter: ColorMatrixFilter | null = null;
  private filterKey = '';
  private backgroundKey = '';
  private graphics = new Graphics();
  private clip: Graphics | null = null;
  private bar = new Graphics();
  private scrollY = 0;
  private extent = 0;
  private viewHeight = 0;
  private handlers = new Map<string, ((e: any) => void)[]>();
  boxWidth = 0;
  boxHeight = 0;
  preferred: Size = { w: 0, h: 0 };
  protected hovered = false;
  protected pressed = false;
  private lastClick = 0;
  private effectiveStyle: UiStyle | null = null;
  private measurements = new Map<string, { size: Size; children: Size[] }>();
  private layoutVersion = 0;
  private arranged = '';
  private hitOrder: Widget[] = [];
  private hitBounds = new Rectangle();
  private hasHitTarget = false;
  constructor(readonly kind = 'box', options: WidgetOptions = {}) {
    super();
    liveWidgets.add(this);
    this.addChild(this.visualLayer, this.contentLayer, this.bar);
    this.visualLayer.addChild(this.graphics);
    this.visualLayer.eventMode = 'none'; this.bar.eventMode = 'none';
    this.style = new Proxy({} as UiStyle, { set: (target, key: string, v: string) => {
      if (target[key] !== v) {
        const previous = target[key]; target[key] = v;
        if (key === 'opacity') { this.effectiveStyle = null; this.alpha = this.disabled ? 0.6 : numeric(v, 1) ?? 1; }
        else if (['background', 'borderColor', 'filter'].includes(key)) { this.effectiveStyle = null; this.refreshVisual(); }
        else if ((key === 'left' || key === 'top') && previous?.endsWith('px') && v?.endsWith('px') && this.arranged && !this.hasHitTarget && (this.classList.contains('tooltip') || this.classList.contains('held'))) {
          this.effectiveStyle = null;
          const [t, , , l] = padding(this.parentWidget?.effective() ?? {}), transform = this.effective().transform ?? '';
          if (key === 'left') this.x = Math.round(l + parseFloat(v) - (transform.includes('translate(') || transform.includes('translateX(-50%)') ? this.boxWidth / 2 : 0));
          else this.y = Math.round(t + parseFloat(v) - (transform.includes('translate(') || transform.includes('translateY(-50%)') ? this.boxHeight * (transform.includes('-55%') ? .55 : .5) : 0));
        } else if (((key === 'width' && this.classList.contains('bar-fill')) || (key === 'height' && this.classList.contains('cooldown'))) && this.parentWidget && this.arranged && v?.endsWith('%')) {
          this.effectiveStyle = null; this.measurements.clear(); this.layoutVersion++;
          const parent = this.parentWidget, [t, r, b, l] = padding(parent.effective());
          if (key === 'width') this.arrange(this.x, this.y, (parent.boxWidth - l - r) * parseFloat(v) / 100, this.boxHeight);
          else {
            const height = (parent.boxHeight - t - b) * parseFloat(v) / 100;
            this.arrange(this.x, this.y + this.boxHeight - height, this.boxWidth, height);
          }
        } else this.invalidateStyle();
      }
      return true;
    } });
    this.className = options.class ?? '';
    Object.assign(this.style, options.style ?? {});
    for (const [key, value] of Object.entries(options)) {
      if (key.startsWith('on') && typeof value === 'function') this.addEventListener(key.slice(2) as keyof UiEvents, value as any);
      else if (!['class', 'style', 'login', 'autocomplete', 'spellcheck'].includes(key) && value !== undefined) {
        if (key === 'name') this.nameHint = String(value);
        else (this as any)[key] = value;
      }
    }
    this.eventMode = 'static';
    const events: Record<string, keyof UiEvents> = { pointerdown: 'mousedown', pointermove: 'mousemove', pointerover: 'mouseenter', pointerout: 'mouseleave', rightclick: 'contextmenu', wheel: 'wheel' };
    for (const [pixiName, name] of Object.entries(events)) this.on(pixiName as any, (e: any) => {
      const event = this.pointerEvent(e);
      if (name === 'mouseenter') this.hovered = true;
      if (name === 'mouseleave') { this.hovered = false; this.pressed = false; }
      if (name === 'mousedown') { this.pressed = true; if (this.disabled) return; }
      if (name === 'wheel' && this.extent > this.viewHeight) { this.scrollTop += e.deltaY / uiScale; e.stopPropagation(); }
      this.dispatch(name, event);
      if (name === 'mouseenter' || name === 'mouseleave' || name === 'mousedown') this.refreshVisual();
    });
    this.on('pointertap', e => {
      if (this.disabled || e.button !== 0) return;
      this.pressed = false;
      this.dispatch('click', this.pointerEvent(e));
      const now = performance.now();
      if (now - this.lastClick < 350) this.dispatch('dblclick', this.pointerEvent(e));
      this.lastClick = now;
      this.refreshVisual();
    });
    this.on('pointerdown', e => {
      if (this.extent <= this.viewHeight || !(this.effective().overflowY === 'auto' || this.effective().overflow === 'auto')) return;
      if (this.toLocal(e.global).x >= this.boxWidth - 5) {
        const thumb = Math.max(6, this.viewHeight * this.viewHeight / this.extent);
        this.scrollDrag = { y: e.global.y, origin: this.scrollTop, factor: (this.extent - this.viewHeight) / Math.max(1, this.viewHeight - thumb) };
        e.stopPropagation();
      }
    });
    this.on('globalpointermove', e => { if (this.scrollDrag) this.scrollTop = this.scrollDrag.origin + (e.global.y - this.scrollDrag.y) / uiScale * this.scrollDrag.factor; });
    this.on('pointerup', () => { this.pressed = false; this.scrollDrag = null; this.refreshVisual(); });
    this.on('pointerupoutside', () => { this.pressed = false; this.scrollDrag = null; this.refreshVisual(); });
  }
  private pointerEvent(e: any): UiPointerEvent {
    const target = e.target instanceof Widget ? e.target : this;
    return { target, clientX: e.global.x, clientY: e.global.y, button: e.button ?? 0, deltaY: e.deltaY ?? 0,
      shiftKey: e.shiftKey ?? false, ctrlKey: e.ctrlKey ?? false,
      preventDefault: () => e.preventDefault(), stopPropagation: () => e.stopPropagation() };
  }
  get className(): string { return this.names; }
  set className(value: string) { if (value !== this.names) { this.names = [...new Set(value.split(/\s+/).filter(Boolean))].join(' '); this.invalidateStyle(); } }
  get parentWidget(): Widget | null { return this.parent?.parent instanceof Widget ? this.parent.parent : null; }
  get isShown(): boolean { return this.visible && this.effective().display !== 'none' && (this.parentWidget?.isShown ?? true); }
  get isConnected(): boolean { return this instanceof UiRoot || !!this.parentWidget?.isConnected; }
  get offsetWidth(): number { activeRoot?.layout(); return this.boxWidth; }
  get offsetHeight(): number { activeRoot?.layout(); return this.boxHeight; }
  get textContent(): string { return this.valueText + this.widgets.map(w => w.textContent).join(''); }
  set textContent(value: string | null) {
    const next = value ?? '';
    if (!this.widgets.length && next === this.valueText) return;
    this.replaceChildren(); this.valueText = next; this.invalidate();
  }
  get value(): string { return this.valueText; }
  set value(value: string) { this.valueText = String(value); this.invalidate(); }
  get scrollTop(): number { return this.scrollY; }
  set scrollTop(v: number) { this.scrollY = Math.max(0, v); this.invalidate(); }
  get firstChild(): Widget | null { return this.widgets[0] ?? null; }
  invalidate(): void {
    for (let w: Widget | null = this; w; w = w.parentWidget) { w.measurements.clear(); w.layoutVersion++; }
    if (this.isConnected && activeRoot) activeRoot.dirty = true;
  }
  /** Inherited presentation changes affect descendants; ordinary content changes do not. */
  invalidateStyle(): void {
    const clear = (w: Widget): void => { w.effectiveStyle = null; w.measurements.clear(); w.layoutVersion++; for (const c of w.widgets) clear(c); };
    clear(this); this.invalidate();
  }
  refreshVisual(): void { if (!this.destroyed && (this.boxWidth || this.boxHeight)) this.paint(this.effective()); }
  append(...children: Child[]): void {
    for (const child of children) {
      if (child === null || child === undefined || child === false || child === '') continue;
      const w = child instanceof Widget ? child : new Label(String(child));
      w.detach(); this.widgets.push(w); this.contentLayer.addChild(w); w.invalidateStyle();
    }
    this.invalidate();
  }
  prepend(...children: Child[]): void { const old = [...this.widgets]; this.replaceChildren(...children, ...old); }
  replaceChildren(...children: Child[]): void {
    for (const w of [...this.widgets]) { w.detach(); retired.add(w); }
    this.valueText = ''; this.append(...children); this.invalidate();
  }
  private detach(): void {
    const p = this.parentWidget;
    if (p) { const i = p.widgets.indexOf(this); if (i >= 0) p.widgets.splice(i, 1); }
    p?.invalidate(); this.removeFromParent(); this.invalidateStyle();
  }
  remove(): void { this.detach(); if (focus.current === this) this.blur(); }
  dispose(): void {
    if (this.destroyed) return;
    liveWidgets.delete(this); retired.delete(this);
    for (const w of [...this.widgets]) w.dispose();
    this.remove(); this.colorFilter?.destroy(); this.destroy({ children: true });
  }
  /** Retire unmounted rebuild widgets after the frame, so reused inputs survive replaceChildren. */
  collect(): void {
    for (const w of retired) {
      if (w.isConnected || w.classList.contains('window') || w instanceof TextInput) retired.delete(w);
      else { w.dispose(); retired.delete(w); }
    }
  }
  addEventListener<K extends keyof UiEvents>(type: K, fn: (e: UiEvents[K]) => void, _options?: unknown): void {
    const list = this.handlers.get(type) ?? []; if (!list.includes(fn)) list.push(fn); this.handlers.set(type, list);
  }
  dispatch(type: string, e: any): void { for (const fn of this.handlers.get(type) ?? []) fn(e); }
  focus(): void { focus.set(this); }
  blur(): void { if (focus.current === this) focus.set(null); }
  closest(selector: string): Widget | null { for (let w: Widget | null = this; w; w = w.parentWidget) if (w.matches(selector)) return w; return null; }
  matches(selector: string): boolean { return selector === '*' ? true : selector.startsWith('.') ? this.classList.contains(selector.slice(1)) : this.kind === selector; }
  querySelectorAll(selector: string): Widget[] { return this.widgets.flatMap(w => [...(w.matches(selector) ? [w] : []), ...w.querySelectorAll(selector)]); }
  querySelector(selector: string): Widget | null { return this.querySelectorAll(selector)[0] ?? null; }
  effective(): UiStyle {
    if (this.effectiveStyle) return this.effectiveStyle;
    const parent = this.parentWidget;
    const inherited = parent?.effective() ?? {};
    const own = presentation(this.names.split(/\s+/), this.kind, parent?.names.split(/\s+/) ?? []);
    const style: UiStyle = { color: inherited.color, fontWeight: inherited.fontWeight, fontFactor: inherited.fontFactor, whiteSpace: inherited.whiteSpace, textOutline: inherited.textOutline, ...own, ...this.style };
    const chat = this.closest('.chat');
    if (chat && !chat.classList.contains('open')) {
      if (this.classList.contains('chat-tabs') || this.classList.contains('chat-input')) style.display = 'none';
      if (this.classList.contains('chat-log')) style.overflowY = 'hidden'; // no scrollbar beside the fading lines
    }
    if (this.closest('.tooltip') || this.closest('.held')) style.pointerEvents = 'none';
    if (chat?.classList.contains('open') && this.classList.contains('chat-log')) style.pointerEvents = 'auto';
    return this.effectiveStyle = style;
  }
  /** Whether the value is drawn as the widget's text (a slider's value is its thumb, a text box draws its own). */
  protected drawsValue(): boolean { return !!this.valueText && !(this instanceof TextInput); }
  isAbsolute(s = this.effective()): boolean { return s.position === 'absolute' || s.position === 'fixed'; }
  private configureText(s: UiStyle, available: number): void {
    if (!this.labelText) { this.labelText = new Text({ textureStyle: { scaleMode: 'linear' } }); this.labelText.eventMode = 'none'; this.visualLayer.addChild(this.labelText); }
    const fs = textPixels() * Number(s.fontFactor ?? 1);
    this.labelText.resolution = textResolution(uiScale);
    this.labelText.text = this.valueText;
    const key = [fs, s.fontWeight, s.color, s.whiteSpace, available, s.textAlign, s.textOutline, uiScale, textResolution()].join('|');
    if (key === this.labelStyleKey) return;
    this.labelStyleKey = key;
    this.labelText.style = { fontFamily: 'Fira Sans', fontSize: fs, fontWeight: (s.fontWeight ?? '400') as any,
      fill: color(s.color), wordWrap: s.whiteSpace !== 'nowrap', breakWords: true, wordWrapWidth: Math.max(1, available),
      lineHeight: fs * 1.3, align: (s.textAlign ?? 'left') as any, ...textEdge(!!s.textOutline) };
  }
  measure(maxW: number, maxH: number): Size {
    const cacheKey = `${maxW}|${maxH}`;
    const cached = this.measurements.get(cacheKey);
    if (cached) {
      this.widgets.forEach((child, i) => child.preferred = cached.children[i]);
      return this.preferred = cached.size;
    }
    const s = this.effective(); this.visible = s.display !== 'none';
    if (!this.visible) return this.preferred = { w: 0, h: 0 };
    const [t, r, b, l] = padding(s), gap = numeric(s.gap, maxW) ?? 0;
    const limitW = Math.max(1, Math.min(maxW, numeric(s.width, maxW) ?? maxW, numeric(s.maxWidth, maxW) ?? maxW));
    const available = Math.max(1, limitW - r - l);
    let w = 0, h = 0;
    if (this.drawsValue()) { this.configureText(s, available); w = this.labelText!.width; h = this.labelText!.height; }
    const children = this.widgets.filter(c => !c.isAbsolute());
    const sizes = children.map(c => c.measure(available, maxH));
    if (s.direction === 'row' && sizes.length) {
      const rects = flex(children.map((c, i) => ({ ...sizes[i], grow: growOf(c), minW: minWidthOf(c, sizes[i].w, available), minH: 0, align: c.effective().alignSelf })),
        available, maxH, true, gap, s.alignItems ?? 'stretch');
      rects.forEach((rect, i) => { sizes[i] = children[i].measure(Math.max(1, rect.w), maxH); });
    }

    if (s.direction === 'row' || s.direction === 'flow') {
      w = Math.max(w, sizes.reduce((n, c) => n + c.w, 0) + Math.max(0, sizes.length - 1) * gap);
      h = Math.max(h, ...sizes.map(c => c.h), 0);
      if (s.direction === 'flow' && w > available) { h *= Math.ceil(w / available); w = available; }
    } else if (s.direction === 'grid') {
      const cols = Number(/repeat\((\d+)/.exec(s.gridTemplateColumns ?? '')?.[1] ?? 5);
      const cw = Math.max(0, ...sizes.map(c => c.w)), ch = Math.max(0, ...sizes.map(c => c.h));
      w = cols * cw + (cols - 1) * gap; h = Math.ceil(sizes.length / cols) * (ch + gap) - gap;
    } else {
      w = Math.max(w, ...sizes.map(c => c.w), 0); h += sizes.reduce((n, c) => n + c.h, 0) + Math.max(0, sizes.length - 1) * gap;
    }
    if (this instanceof TextInput) { w = 100; h = 12; }
    w += l + r; h += t + b;
    w = (s.width?.endsWith('%') ? undefined : numeric(s.width, maxW)) ?? Math.max(w, numeric(s.minWidth, maxW) ?? 0);
    h = (s.height?.endsWith('%') ? undefined : numeric(s.height, maxH)) ?? Math.max(h, numeric(s.minHeight, maxH) ?? 0);
    w = Math.min(w, numeric(s.maxWidth, maxW) ?? maxW, maxW);
    h = Math.min(h, numeric(s.maxHeight, maxH) ?? maxH, maxH);
    const size = { w: Math.max(0, w), h: Math.max(0, h) };
    // Bound the constraint cache instead of retaining a history of viewport sizes.
    if (this.measurements.size >= 8) this.measurements.clear();
    this.measurements.set(cacheKey, { size, children: this.widgets.map(c => c.preferred) });
    return this.preferred = size;
  }
  arrange(x: number, y: number, w: number, h: number): void {
    const key = `${x}|${y}|${w}|${h}|${this.layoutVersion}`;
    if (this.arranged === key) return;
    this.arranged = key;
    const s = this.effective(); this.visible = s.display !== 'none'; if (!this.visible) return;
    this.position.set(Math.round(x), Math.round(y)); this.boxWidth = Math.max(0, w); this.boxHeight = Math.max(0, h);
    const [t, r, b, l] = padding(s), cw = Math.max(0, w - l - r), ch = Math.max(0, h - t - b), gap = numeric(s.gap, cw) ?? 0;
    this.hitArea = new Rectangle(0, 0, w, h);
    this.eventMode = s.pointerEvents === 'none' || this.disabled ? 'passive' : 'static';
    this.cursor = this.disabled ? 'default' : s.cursor as any;
    this.alpha = this.disabled ? 0.6 : numeric(s.opacity, 1) ?? 1;
    this.zIndex = Number(s.zIndex ?? 0);
    this.contentLayer.sortableChildren = true;
    if (this.drawsValue()) {
      this.configureText(s, cw);
      if (s.whiteSpace === 'nowrap' && this.labelText!.width > cw) {
        let text = this.valueText;
        while (text.length && this.labelText!.width > cw) { text = text.slice(0, -1); this.labelText!.text = text + '...'; }
      }
      this.labelText!.position.set(l + (s.textAlign === 'center' || s.justifyContent === 'center' ? Math.max(0, (cw - this.labelText!.width) / 2) : s.textAlign === 'right' ? Math.max(0, cw - this.labelText!.width) : 0), t + (s.alignItems === 'center' ? Math.max(0, (ch - this.labelText!.height) / 2) : 0));
    } else if (this.labelText && !(this instanceof TextInput)) this.labelText.text = '';
    const children = this.widgets.filter(c => !c.isAbsolute() && c.visible);
    let extent = ch;
    if (s.direction === 'grid') {
      const cols = Number(/repeat\((\d+)/.exec(s.gridTemplateColumns ?? '')?.[1] ?? 5);
      const cellW = Math.min((cw - (cols - 1) * gap) / cols, Math.max(0, ...children.map(c => c.preferred.w)));
      const cellH = Math.max(0, ...children.map(c => c.preferred.h));
      children.forEach((c, i) => c.arrange(l + i % cols * (cellW + gap), t + Math.floor(i / cols) * (cellH + gap), cellW, cellH));
      extent = Math.ceil(children.length / cols) * (cellH + gap) - gap;
    } else if (s.direction === 'flow') {
      // Wrapped lines, each aligned by the container's textAlign (its words are child labels).
      const lines: { items: Widget[]; w: number; h: number }[] = [{ items: [], w: 0, h: 0 }];
      children.forEach(c => { let line = lines[lines.length - 1];
        if (line.w && line.w + c.preferred.w > cw) lines.push(line = { items: [], w: 0, h: 0 });
        line.items.push(c); line.w += c.preferred.w + gap; line.h = Math.max(line.h, c.preferred.h); });
      let fy = 0;
      lines.forEach((line, i) => {
        const free = Math.max(0, cw - (line.w - gap));
        let fx = s.textAlign === 'center' ? free / 2 : s.textAlign === 'right' ? free : 0;
        for (const c of line.items) { c.arrange(l + fx, t + fy, Math.min(cw, c.preferred.w), c.preferred.h); fx += c.preferred.w + gap; }
        fy += line.h + (i < lines.length - 1 ? gap : 0);
      });
      extent = fy;
    } else {
      const row = s.direction === 'row';
      extent = row ? ch : children.reduce((n, c) => n + c.preferred.h, 0) + Math.max(0, children.length - 1) * gap;
      const scroll = s.overflowY === 'auto' || s.overflow === 'auto';
      const rects = flex(children.map(c => ({ w: c.effective().width?.endsWith('%') && row ? numeric(c.effective().width, cw) ?? c.preferred.w : c.preferred.w, h: c.effective().height?.endsWith('%') ? numeric(c.effective().height, ch) ?? c.preferred.h : c.preferred.h, grow: growOf(c),
        minW: minWidthOf(c, c.preferred.w, cw), minH: scroll && !row ? c.preferred.h : 0, align: c.effective().alignSelf })), cw, scroll && !row ? Math.max(ch, extent) : ch, row, gap, s.alignItems ?? 'stretch', s.justifyContent ?? 'start');
      rects.forEach((rect, i) => { children[i].measure(Math.max(1, rect.w), Math.max(1, rect.h)); children[i].arrange(l + rect.x, t + rect.y, rect.w, rect.h); });
    }
    for (const c of this.widgets.filter(c => c.isAbsolute())) {
      c.measure(cw, ch);
      const cs = c.effective(), inset = cs.inset !== undefined;
      const left = numeric(cs.left, cw), right = numeric(cs.right, cw), top = numeric(cs.top, ch), bottom = numeric(cs.bottom, ch);
      let ww = inset ? cw : left !== undefined && right !== undefined ? Math.max(0, cw - left - right) : numeric(cs.width, cw) ?? c.preferred.w;
      let hh = inset ? ch : top !== undefined && bottom !== undefined ? Math.max(0, ch - top - bottom) : numeric(cs.height, ch) ?? c.preferred.h;
      let xx = left ?? (right !== undefined ? cw - right - ww : 0), yy = top ?? (bottom !== undefined ? ch - bottom - hh : 0);
      const transform = cs.transform ?? '';
      if (transform.includes('translate(')) { xx -= ww / 2; yy -= hh * (transform.includes('-55%') ? .55 : .5); }
      else { if (transform.includes('translateX(-50%)')) xx -= ww / 2; if (transform.includes('translateY(-50%)')) yy -= hh / 2; }
      if (c.classList.contains('window') || c.classList.contains('window-row') || c.classList.contains('tooltip') || c.classList.contains('menu-popup')) {
        ww = Math.min(cw, ww); hh = Math.min(ch, hh); xx = clamp(xx, 0, cw - ww); yy = clamp(yy, 0, ch - hh);
      }
      c.arrange(l + xx, t + yy, ww, hh);
    }
    this.extent = Math.max(ch, extent); this.viewHeight = ch;
    const clipping = s.overflowY === 'auto' || s.overflow === 'hidden' || s.overflow === 'auto';
    this.scrollY = clamp(this.scrollY, 0, this.extent - ch);
    this.contentLayer.y = clipping ? -this.scrollY : 0;
    if (clipping) {
      if (!this.clip) { this.clip = new Graphics(); this.clip.eventMode = 'none'; this.addChild(this.clip); this.contentLayer.mask = this.clip; }
      this.clip.clear().rect(l, t, cw, ch).fill(0xffffff);
    } else if (this.clip) { this.contentLayer.mask = null; this.clip.destroy(); this.clip = null; }
    this.bar.clear();
    if ((s.overflowY === 'auto' || s.overflow === 'auto') && this.extent > ch && ch > 0) {
      const thumb = Math.max(6, ch * ch / this.extent), by = t + (ch - thumb) * this.scrollY / (this.extent - ch);
      this.bar.rect(w - 3, t, 2, ch).fill('#281e1b').rect(w - 3, by, 2, thumb).fill('#c99a50');
    }
    this.hitOrder = [...this.widgets].reverse().sort((a, b) => b.zIndex - a.zIndex);
    this.hasHitTarget = s.pointerEvents !== 'none' || this.widgets.some(c => c.hasHitTarget);
    let left = 0, top = 0, right = w, bottom = h;
    if (!clipping) for (const c of this.widgets) {
      if (!c.visible || !c.hasHitTarget) continue;
      left = Math.min(left, c.x + c.hitBounds.x); top = Math.min(top, c.y + c.hitBounds.y);
      right = Math.max(right, c.x + c.hitBounds.x + c.hitBounds.width); bottom = Math.max(bottom, c.y + c.hitBounds.y + c.hitBounds.height);
    }
    this.hitBounds = new Rectangle(left, top, right - left, bottom - top);
    this.paint(s);
  }
  protected paint(s: UiStyle): void {
    const w = this.boxWidth, h = this.boxHeight;
    const filter = s.filter ?? '';
    if (filter !== this.filterKey) {
      this.colorFilter?.destroy(); this.colorFilter = null;
      if (filter) { this.colorFilter = new ColorMatrixFilter(); if (filter.includes('grayscale')) this.colorFilter.desaturate(); else if (filter.includes('sepia')) this.colorFilter.tint('#ee5555'); }
      this.filters = this.colorFilter ? [this.colorFilter] : []; this.filterKey = filter;
    }
    let key = '', border = 3;
    const has = (name: string) => this.classList.contains(name);
    if (has('panel')) { key = 'panel'; border = 6; }
    if (has('well') || has('card')) key = 'well';
    if (has('slot') || has('list-row')) key = this.hovered || has('selected') ? 'slot_hover' : 'slot';
    if (this.kind === 'button') { key = this.pressed || has('active') ? 'button_down' : this.hovered ? 'button_hover' : 'button'; border = 4; }
    if (this instanceof TextInput) key = focus.current === this ? 'textbox_focus' : 'textbox';
    if (has('chat-box') && !this.closest('.chat')?.classList.contains('open')) key = '';
    if (skin && key !== this.backgroundKey) {
      this.background?.destroy(); this.background = key ? new NineSliceSprite({ texture: skin.uiCell(key), leftWidth: border, rightWidth: border, topHeight: border, bottomHeight: border }) : null;
      if (this.background) { this.background.eventMode = 'none'; this.visualLayer.addChildAt(this.background, 0); }
      this.backgroundKey = key;
    }
    if (this.background) { this.background.width = Math.max(border * 2, w); this.background.height = Math.max(border * 2, h); }
    this.graphics.clear();
    const bg = s.background;
    if (bg && !bg.includes('gradient') && !bg.includes('var(--img')) {
      const rgba = /rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/.exec(bg);
      const fill = rgba ? { color: (Number(rgba[1]) << 16) | (Number(rgba[2]) << 8) | Number(rgba[3]), alpha: Number(rgba[4]) } : color(bg, '#000000');
      this.graphics.rect(0, 0, w, h).fill(fill);
    }
    if (has('quality')) this.graphics.rect(.5, .5, Math.max(0, w - 1), Math.max(0, h - 1)).stroke({ color: color(s.borderColor), width: 1, alpha: .8 });
    if (has('exp-ticks')) for (let i = 1; i < 10; i++) this.graphics.rect(Math.floor(w * i / 10), 0, .5, h).fill({ color: 0x0b090c, alpha: .5 });
  }
  containsPoint(x: number, y: number): boolean {
    if (!this.visible || this.alpha <= 0.01) return false;
    const p = this.toLocal({ x, y }); return p.x >= 0 && p.y >= 0 && p.x < this.boxWidth && p.y < this.boxHeight;
  }
  hitWidget(x: number, y: number): Widget | null {
    if (!this.visible || this.alpha <= .01 || !this.hasHitTarget) return null;
    const p = this.toLocal({ x, y });
    if (!this.hitBounds.contains(p.x, p.y)) return null;
    const s = this.effective();
    const inside = p.x >= 0 && p.y >= 0 && p.x < this.boxWidth && p.y < this.boxHeight;
    if ((s.overflowY === 'auto' || s.overflow === 'hidden' || s.overflow === 'auto') && !inside) return null;
    for (const child of this.hitOrder) { const hit = child.hitWidget(x, y); if (hit) return hit; }
    if (s.pointerEvents !== 'none' && inside) return this;
    return null;
  }
  tick(now: number): void { for (const c of this.widgets) c.tick(now); }
}

/** Label text's shadow; an outline too keeps it readable over the bright world (the chat while it is closed). */
function textEdge(outline: boolean) {
  return outline
    ? { stroke: { color: '#080608', width: 2.5 / uiScale, join: 'round' as const }, dropShadow: { color: '#080608', alpha: 0.8, blur: 0, distance: 1.5 / uiScale, angle: Math.PI / 2 } }
    : { dropShadow: { color: '#080608', alpha: 0.55, blur: 0, distance: 1 / uiScale, angle: Math.PI / 2 } };
}
/** The width a label draws one unwrapped line of text at, in art pixels. */
export function textWidth(text: string, fontWeight = '400', outline = false, fontFactor = 1): number {
  const key = `${textPixels()}|${uiScale}|${fontWeight}|${outline}|${fontFactor}`;
  let style = measureStyles.get(key);
  if (!style) measureStyles.set(key, style = new TextStyle({ fontFamily: 'Fira Sans', fontSize: textPixels() * fontFactor, fontWeight: fontWeight as any, ...textEdge(outline) }));
  return CanvasTextMetrics.measureText(text, style).width;
}
const measureStyles = new Map<string, TextStyle>();

export class Label extends Widget { constructor(text = '', options: WidgetOptions = {}) { super('label', options); this.valueText = text; this.style.pointerEvents = 'none'; } }
export class Panel extends Widget { constructor(options: WidgetOptions = {}) { super('box', { ...options, class: `panel ${options.class ?? ''}` }); } }
export class Well extends Widget { constructor(options: WidgetOptions = {}) { super('box', { ...options, class: `well ${options.class ?? ''}` }); } }
export class Button extends Widget { constructor(options: WidgetOptions = {}) { super('button', { ...options, class: `button ${options.class ?? ''}` }); } }
export class Grid extends Widget { constructor(options: WidgetOptions = {}) { super('box', { ...options, class: `grid ${options.class ?? ''}` }); } }
export class List extends Widget { constructor(options: WidgetOptions = {}) { super('box', { ...options, class: `list ${options.class ?? ''}` }); } }
export class ScrollView extends Widget {
  constructor(options: WidgetOptions = {}) { super('box', { ...options, class: `scroll ${options.class ?? ''}` }); }
}
export class Window extends ScrollView { constructor(options: WidgetOptions = {}) { super({ ...options, class: `panel window ${options.class ?? ''}` }); } }
export class RichText extends Widget { constructor(options: WidgetOptions = {}) { super('text', options); } }
export class Tabs extends Widget { constructor(options: WidgetOptions = {}) { super('box', { ...options, class: `tabs ${options.class ?? ''}` }); } }
export class Slot extends Widget { constructor(options: WidgetOptions = {}) { super('box', { ...options, class: `slot ${options.class ?? ''}` }); } }
export class Bar extends Widget { constructor(options: WidgetOptions = {}) { super('box', { ...options, class: `bar ${options.class ?? ''}` }); } }

export class TextInput extends Widget {
  readonly editor = new EditModel();
  /** Lines the touch keyboard shows above the preview while typing here (chat: the latest messages). */
  keyboardContext: (() => { text: string; color: string }[]) | null = null;
  private editing = new Graphics();
  private maskShape = new Graphics();
  private offset = 0;
  private editStyleKey = '';
  private editDrawKey = '';
  private selecting = false;
  constructor(options: WidgetOptions = {}) {
    super('input', options);
    this.editor.maxLength = this.maxLength;
    this.editor.set(this.valueText);
    this.visualLayer.addChild(this.editing, this.maskShape);
    this.editing.eventMode = this.maskShape.eventMode = 'none';
    this.labelText = new Text({ textureStyle: { scaleMode: 'linear' } }); this.labelText.eventMode = 'none'; this.visualLayer.addChild(this.labelText);
    this.labelText.mask = this.maskShape;
    this.style.cursor = 'text';
    this.on('pointerdown', e => { if (e.button !== 0) return; this.focus(); this.editor.select(this.indexAt(e), e.shiftKey); this.selecting = true; this.invalidate(); });
    this.on('globalpointermove', e => { if (this.selecting) { this.editor.select(this.indexAt(e), true); this.invalidate(); } });
    this.on('pointerup', () => this.selecting = false); this.on('pointerupoutside', () => this.selecting = false);
    this.addEventListener('dblclick', e => { this.editor.selectWord(this.indexAt({ global: { x: e.clientX, y: e.clientY } })); this.invalidate(); });
  }
  override get value(): string { return this.editor?.text ?? this.valueText; }
  override set value(v: string) { if (this.editor) { this.editor.maxLength = this.maxLength; this.editor.set(v); } else this.valueText = v; this.invalidate(); }
  display(): string { return this.type === 'password' ? '•'.repeat(this.editor.text.length) : this.editor.text; }
  private advance(index: number): number {
    if (!index) return 0;
    return CanvasTextMetrics.measureText(this.display().slice(0, index), this.labelText!.style).width;
  }
  private indexAt(e: { global: { x: number; y: number } }): number {
    const x = this.toLocal(e.global).x - 5 + this.offset;
    for (let i = 0; i < this.editor.text.length; i++) if (x < (this.advance(i) + this.advance(i + 1)) / 2) return i;
    return this.editor.text.length;
  }
  key(e: KeyboardEvent): void {
    this.editor.maxLength = this.maxLength;
    this.dispatch('keydown', e);
    if (!this.isConnected || focus.current !== this) return;
    if (e.defaultPrevented) return;
    if (e.key === 'Tab') { focus.next(e.shiftKey); e.preventDefault(); }
    else if (e.key === 'Escape') { this.blur(); e.preventDefault(); }
    else if (e.key === 'Enter') { this.closest('form')?.dispatch('submit', e); e.preventDefault(); }
    else if (this.editor.key(e)) { this.dispatch('input', new Event('input')); e.preventDefault(); }
    this.invalidate();
  }
  override tick(now: number): void { if (focus.current === this) this.drawEdit(now); }
  protected override paint(s: UiStyle): void { super.paint(s); this.drawEdit(performance.now()); }
  private drawEdit(now: number): void {
    const text = this.labelText!, focused = focus.current === this;
    const fill = color(this.editor.text ? this.effective().color : '#a19a90');
    const drawKey = [this.editor.text, this.editor.start, this.editor.end, this.editor.caret, this.type, this.placeholder,
      this.boxWidth, this.boxHeight, uiScale, textResolution(), textPixels(), fill, focused, focused ? Math.floor(now / 500) % 2 : 0].join('|');
    if (drawKey === this.editDrawKey) return;
    this.editDrawKey = drawKey;
    const key = `${uiScale}|${textResolution()}|${textPixels()}|${fill}`;
    if (key !== this.editStyleKey) {
      text.resolution = textResolution(uiScale); text.style = { fontFamily: 'Fira Sans', fontSize: textPixels(), fill, lineHeight: textPixels() * 1.3 };
      this.editStyleKey = key;
    }
    text.text = this.display() || this.placeholder;
    const width = Math.max(0, this.boxWidth - 10), caret = this.advance(this.editor.caret);
    if (focused) this.offset = clamp(this.offset, Math.max(0, caret - width + 1), Math.max(0, caret));
    else this.offset = 0;
    text.position.set(5 - this.offset, Math.max(3, (this.boxHeight - text.height) / 2));
    this.maskShape.clear().rect(5, 2, width, Math.max(0, this.boxHeight - 4)).fill(0xffffff);
    this.editing.clear();
    if (focused) {
      const a = clamp(this.advance(this.editor.start) - this.offset, 0, width), b = clamp(this.advance(this.editor.end) - this.offset, 0, width);
      if (a !== b) this.editing.rect(5 + a, 3, b - a, this.boxHeight - 6).fill({ color: '#e8c068', alpha: .35 });
      if (Math.floor(now / 500) % 2 === 0) this.editing.rect(5 + caret - this.offset, 3, .5, this.boxHeight - 6).fill('#f6ecd8');
    }
  }
}

export class Slider extends Widget {
  private track = new Graphics();
  protected override drawsValue(): boolean { return false; }
  constructor(options: WidgetOptions = {}) {
    super('slider', options); this.style.minWidth = '60px'; this.style.height = '14px'; this.style.cursor = 'pointer';
    this.visualLayer.addChild(this.track); this.track.eventMode = 'none';
    let dragging = false;
    const update = (e: FederatedPointerEvent) => { const p = this.toLocal(e.global); this.value = String(Math.round(this.min + clamp(p.x / Math.max(1, this.boxWidth), 0, 1) * (this.max - this.min))); this.dispatch('input', new Event('input')); };
    this.on('pointerdown', e => { dragging = true; update(e); }); this.on('globalpointermove', e => { if (dragging) update(e); });
    this.on('pointerup', () => dragging = false); this.on('pointerupoutside', () => dragging = false);
  }
  protected override paint(s: UiStyle): void { super.paint(s); const g = this.track; g.clear();
    g.rect(0, this.boxHeight / 2 - 1, this.boxWidth, 2).fill('#5a4632');
    const x = clamp((Number(this.value) - this.min) / (this.max - this.min), 0, 1) * Math.max(0, this.boxWidth - 5);
    g.rect(x, 1, 5, this.boxHeight - 2).fill('#e8c068');
  }
}
export class Checkbox extends Button {
  // A fixed width, so [x] and [ ] line up.
  constructor(options: WidgetOptions = {}) { super(options); this.style.minWidth = '3.6em'; this.addEventListener('click', () => { this.checked = !this.checked; this.textContent = this.checked ? '[x]' : '[ ]'; this.dispatch('input', new Event('input')); }); }
}

class FocusManager {
  current: Widget | null = null;
  /** Told whenever focus moves (the on-screen keyboard follows it). */
  readonly changed = new Set<(w: Widget | null) => void>();
  set(next: Widget | null): void {
    if (next === this.current) return;
    const old = this.current; this.current = next; old?.dispatch('blur', new Event('blur')); old?.refreshVisual(); next?.refreshVisual();
    for (const fn of this.changed) fn(next);
  }
  next(backwards: boolean): void {
    const fields = activeRoot?.all().filter(w => w instanceof TextInput && w.isShown && !w.disabled) ?? [];
    const i = fields.indexOf(this.current!); this.set(fields[(i + (backwards ? -1 : 1) + fields.length) % fields.length] ?? null);
  }
  install(canvas: HTMLCanvasElement): void {
    window.addEventListener('keydown', e => {
      if (this.current instanceof TextInput) { this.current.key(e); e.stopImmediatePropagation(); }
    }, { capture: true });
    window.addEventListener('blur', () => this.current?.blur());
    canvas.addEventListener('pointerdown', e => {
      const hit = activeRoot?.hitTest(e.clientX, e.clientY);
      if (!(hit instanceof TextInput)) this.set(null);
    }, { capture: true });
    for (const type of ['paste', 'copy', 'cut'] as const) window.addEventListener(type, e => {
      const input = this.current; if (!(input instanceof TextInput)) return;
      if (type === 'paste') { input.editor.insert(e.clipboardData?.getData('text/plain') ?? ''); input.dispatch('input', new Event('input')); }
      else if (input.type !== 'password') {
        e.clipboardData?.setData('text/plain', input.editor.text.slice(input.editor.start, input.editor.end));
        if (type === 'cut' && input.editor.start !== input.editor.end) { input.editor.delete(-1); input.dispatch('input', new Event('input')); }
      }
      e.preventDefault(); input.invalidate();
    });
  }
}
export const focus = new FocusManager();

export class UiRoot extends Widget {
  readonly overlay = new Widget('overlay', { style: { position: 'absolute', inset: '0', pointerEvents: 'none', zIndex: '100' } });
  dirty = true;
  layoutRevision = 0;
  private revision = -1;
  private viewportW = 0;
  private viewportH = 0;
  /** The latest press began on the UI, or the UI took it (closing a menu): the world must ignore it. Recorded before
   *  any widget handles the press, as a widget may act on press and vanish before the page's own mousedown. */
  pressTaken = false;
  constructor() { super('root', { style: { pointerEvents: 'none' } }); activeRoot = this; this.append(this.overlay); }
  /** Starts recording presses: a window capture listener added before any other, so it runs first. */
  install(): void { window.addEventListener('pointerdown', e => { this.pressTaken = !!this.hitTest(e.clientX, e.clientY); }, { capture: true }); }
  /** Marks the current press as the UI's (a handler that used it without a widget under the pointer). */
  takePress(): void { this.pressTaken = true; }
  clear(): void { focus.set(null); for (const w of [...liveWidgets]) if (w !== this && w !== this.overlay) w.dispose(); retired.clear(); }
  resize(width: number, height: number): void { if (width !== this.viewportW || height !== this.viewportH || this.revision !== scaleRevision) {
    this.viewportW = width; this.viewportH = height; this.revision = scaleRevision; this.scale.set(uiScale); this.invalidateStyle();
  } }
  layout(): void {
    if (!this.dirty || !this.viewportW) return;
    this.dirty = false; const w = this.viewportW / uiScale, h = this.viewportH / uiScale;
    this.measure(w, h); this.arrange(0, 0, w, h); this.layoutRevision++;
  }
  hitTest(x: number, y: number): Widget | null { this.layout(); return this.hitWidget(x, y); }
  all(): Widget[] { return this.widgets.flatMap(w => [w, ...w.querySelectorAll('*')]); }
  find(testId: string): Widget | null { const visit = (w: Widget): Widget | null => w.testId === testId ? w : w.widgets.map(visit).find(Boolean) ?? null; return visit(this); }
  click(testId: string): void { const w = this.find(testId); if (!w || w.disabled) throw new Error(`No enabled widget: ${testId}`); w.dispatch('click', { target: w, button: 0, stopPropagation() {}, preventDefault() {} }); }
  focusField(testId: string): void { const w = this.find(testId); if (!(w instanceof TextInput)) throw new Error(`No text input: ${testId}`); w.focus(); }
  text(): string { return this.textContent; }
}
